import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const LanguageContext = createContext(undefined);

export const LanguageProvider = ({ children }) => {
    const { user, updateUser } = useAuth();
    const [translations, setTranslations] = useState({});
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const hasInitialized = useRef(false);

    // Priority: 1) localStorage, 2) user profile from DB, 3) default 'es'
    const getInitialLang = () => {
        return localStorage.getItem('preferred_lang')
            || user?.preferredLanguage
            || user?.preferred_language_code
            || 'es';
    };

    const [currentLanguage, setCurrentLanguage] = useState(getInitialLang());
    const [loading, setLoading] = useState(false);

    const API_URL = import.meta.env.VITE_API_BASE_URL || '/api';

    // When user loads from server (login / page refresh), use their DB preference
    // ONLY if there's no localStorage override already set by the user
    useEffect(() => {
        if (!user) return;
        const storedLang = localStorage.getItem('preferred_lang');
        const userLang = user.preferredLanguage || user.preferred_language_code;

        if (storedLang) {
            // localStorage wins — user explicitly chose this language
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
            }
        } else if (userLang && userLang !== currentLanguage) {
            // No localStorage preference — use DB value
            setCurrentLanguage(userLang);
            localStorage.setItem('preferred_lang', userLang);
        }
        hasInitialized.current = true;
    }, [user]);

    // Fetch translations whenever language changes
    useEffect(() => {
        const fetchTranslations = async () => {
            if (!currentLanguage) return;

            setLoading(true);
            try {
                const response = await fetch(`${API_URL}/i18n/translations/${currentLanguage}`);
                if (response.ok) {
                    const data = await response.json();
                    setTranslations(data.translations || {});
                }
            } catch (error) {
                console.error('Error loading translations:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTranslations();
    }, [currentLanguage]);

    // Fetch available languages once
    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const response = await fetch(`${API_URL}/i18n/languages`);
                if (response.ok) {
                    const data = await response.json();
                    // Sort alphabetically by native_name
                    const sorted = (data.languages || []).sort((a, b) =>
                        (a.native_name || a.name).localeCompare(b.native_name || b.name)
                    );
                    setAvailableLanguages(sorted);
                }
            } catch (error) {
                console.error('Error fetching languages:', error);
            }
        };
        fetchLanguages();
    }, []);

    const t = (key, defaultValue) => {
        return translations[key] || defaultValue || key;
    };

    const setLanguage = async (lang) => {
        setCurrentLanguage(lang);
        localStorage.setItem('preferred_lang', lang);

        // Update user object in AuthContext so it doesn't reset on re-render
        if (user) {
            updateUser({
                preferredLanguage: lang,
                preferred_language_code: lang
            });
        }

        // Persist to database
        if (user) {
            try {
                const token = localStorage.getItem('auth_token');
                await fetch(`${API_URL}/auth/profile`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ preferred_language_code: lang })
                });
            } catch (error) {
                console.error('Error saving language preference:', error);
            }
        }
    };

    return (
        <LanguageContext.Provider value={{
            t,
            currentLanguage,
            setLanguage,
            loading,
            languages: availableLanguages
        }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
